import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Truck, Clock, Globe, Package } from "lucide-react";

const ShippingPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="shipping-policy-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight" data-testid="page-title">
            Shipping Policy
          </h1>
          <p className="text-zinc-400 mt-4">Last updated: January 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Shipping Options Overview */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                  <Truck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Standard Shipping</h3>
                  <p className="text-primary font-semibold">3-5 Business Days</p>
                </div>
              </div>
              <ul className="text-zinc-400 space-y-2 text-sm">
                <li>• Free on orders over $100</li>
                <li>• Flat rate $7.99 for orders under $100</li>
                <li>• Tracking included</li>
                <li>• Delivery Monday-Saturday</li>
              </ul>
            </div>
            <div className="bg-zinc-900 border border-secondary p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded flex items-center justify-center">
                  <Clock className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Expedited Shipping</h3>
                  <p className="text-secondary font-semibold">1-2 Business Days</p>
                </div>
              </div>
              <ul className="text-zinc-400 space-y-2 text-sm">
                <li>• Flat rate $19.99</li>
                <li>• Priority handling</li>
                <li>• Real-time tracking</li>
                <li>• Order by 2 PM for same-day dispatch</li>
              </ul>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Shipping Rates & Calculation</h2>
              <p className="text-zinc-400 mb-4">
                Shipping rates are calculated in real-time at checkout based on your location, package weight, and selected shipping method. 
                You'll see the exact shipping cost before completing your purchase.
              </p>
              <table className="w-full mt-6">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left text-zinc-400 py-3">Order Value</th>
                    <th className="text-left text-zinc-400 py-3">Standard</th>
                    <th className="text-left text-zinc-400 py-3">Expedited</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-b border-zinc-800">
                    <td className="py-3">Under $50</td>
                    <td className="py-3">$7.99</td>
                    <td className="py-3">$19.99</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3">$50 - $99.99</td>
                    <td className="py-3">$7.99</td>
                    <td className="py-3">$19.99</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3">$100+</td>
                    <td className="py-3 text-primary font-bold">FREE</td>
                    <td className="py-3">$19.99</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-zinc-500 text-sm mt-4">
                * Oversized items (e.g., bumpers, exhausts) may incur additional freight charges displayed at checkout.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Order Processing</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">Processing Time</h3>
                  <p className="text-zinc-400">
                    Orders are processed within 1-2 business days. Orders placed after 2 PM EST or on weekends/holidays 
                    will be processed the next business day.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Order Cutoff</h3>
                  <p className="text-zinc-400">
                    For same-day dispatch on expedited orders, place your order before <strong className="text-white">2:00 PM EST</strong>, 
                    Monday through Friday.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Tracking Your Order</h2>
              <p className="text-zinc-400 mb-4">
                Once your order ships, you'll receive automated tracking updates via:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li><strong className="text-white">Email:</strong> Shipping confirmation with tracking number</li>
                <li><strong className="text-white">Customer Account:</strong> Real-time status updates in your order history</li>
                <li><strong className="text-white">SMS (optional):</strong> Text updates for delivery notifications</li>
              </ul>
              <p className="text-zinc-400 mt-4">
                You can track your package anytime by logging into your account or using the tracking link in your confirmation email.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Shipping Locations</h2>
              <div className="flex items-start gap-4">
                <Globe className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="text-zinc-400 mb-4">
                    We currently ship to all 50 U.S. states. Shipping times and rates may vary for Alaska, Hawaii, 
                    and U.S. territories.
                  </p>
                  <p className="text-zinc-400">
                    <strong className="text-white">P.O. Boxes:</strong> We can ship to P.O. Boxes via USPS for standard shipping only. 
                    Expedited shipping requires a physical address.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Delivery Issues</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-semibold mb-2">Damaged Packages</h3>
                  <p className="text-zinc-400">
                    If your package arrives damaged, please take photos before opening and contact us within 48 hours. 
                    We'll arrange a replacement or refund at no additional cost.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Lost Packages</h3>
                  <p className="text-zinc-400">
                    If your tracking shows delivered but you haven't received your package, please check with neighbors and 
                    your local carrier. If still missing after 48 hours, contact us and we'll investigate.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Wrong Address</h3>
                  <p className="text-zinc-400">
                    Please double-check your shipping address before completing checkout. Address changes may be possible 
                    if the order hasn't shipped yet - contact us immediately.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-primary/10 border border-primary p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Questions About Shipping?</h2>
              <p className="text-zinc-300">
                Our team is happy to help with any shipping questions or special delivery requirements.
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <Link to="/contact" className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-6 py-3 inline-block">
                  Contact Us
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicyPage;
